/**
 * Site-wide GA4 event tracking.
 *
 * Implements the "Custom Events" (add_to_cart_detail, view_cart_detail,
 * begin_checkout_detail, remove_from_cart_detail, upsell_shown/accepted/declined)
 * and browse/discovery events (view_item_list, select_item, component_viewed,
 * component_clicked) from the MakeReady Editions event tracking plan.
 *
 * Native events (page_viewed, product_viewed, add_to_cart, search_submitted,
 * cart_viewed, remove_from_cart, checkout_started, checkout_completed) are
 * out of scope here — those are subscribed via a Shopify Custom Pixel
 * (Admin > Settings > Customer Events), not theme code.
 */
import { ThemeEvents } from '@theme/events';

/**
 * Sends a GA4 event via gtag() (see the gtag.js loader + shim in
 * layout/theme.liquid). The theme loads gtag.js, not a GTM container, so
 * events have to go through gtag('event', ...) — pushing a plain object
 * onto window.dataLayer isn't a format gtag.js recognizes as an event.
 * @param {string} name
 * @param {Record<string, unknown>} [params]
 */
export function pushDataLayerEvent(name, params = {}) {
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', name, params);
}

/** @returns {string | undefined} */
export function currentCurrency() {
  return typeof Theme !== 'undefined' ? Theme.currency : undefined;
}

/**
 * Shapes the `data-analytics-item-*` attributes (from a live element's
 * dataset, or a plain dataset snapshot captured earlier) into a GA4 item.
 * @param {DOMStringMap | Record<string, string> | undefined} dataset
 */
function itemFromDataset(dataset) {
  if (!dataset) return null;

  const { analyticsItemId, analyticsItemName, analyticsItemCategory, analyticsItemVariant, analyticsItemPrice } =
    dataset;
  if (!analyticsItemId) return null;

  return {
    item_id: analyticsItemId || undefined,
    item_name: analyticsItemName || undefined,
    item_category: analyticsItemCategory || undefined,
    item_variant: analyticsItemVariant || undefined,
    price: analyticsItemPrice ? Number(analyticsItemPrice) : undefined,
  };
}

/**
 * Reads the `data-analytics-item-*` attributes off the nearest ancestor
 * (or self) that carries them, e.g. a <product-card> or a PDP/quick-add
 * <product-form-component>. See snippets/product-card.liquid,
 * blocks/buy-buttons.liquid, snippets/quick-add.liquid.
 * @param {Element | null} element
 */
function readItem(element) {
  const source = element?.closest instanceof Function ? element.closest('[data-analytics-item-id]') : null;
  if (!(source instanceof HTMLElement)) return null;

  return itemFromDataset(source.dataset);
}

/** @param {Element | null} element */
function componentSourceFor(element) {
  return element?.closest instanceof Function
    ? element.closest('[data-analytics-cart-source]')?.dataset.analyticsCartSource
    : undefined;
}

/**
 * Wires up all the document-level listeners and observers that drive GA4
 * event tracking. Only called when GA4 is actually configured (see the
 * call site below) — cart-drawer.js and add-to-cart-upsell.js import
 * pushDataLayerEvent/currentCurrency unconditionally, so this module is
 * always fetched and evaluated, but its listeners/observers shouldn't run
 * site-wide when a merchant hasn't set settings.ga4_measurement_id.
 */
function init() {
  /* ------------------------------------------------------------------ */
  /* Cart: add_to_cart_detail / remove_from_cart_detail                  */
  /* ------------------------------------------------------------------ */

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const detail = /** @type {any} */ (event).detail || {};
    const data = detail.data || {};
    const target = event.target instanceof Element ? event.target : null;

    // Every add-to-cart on every surface dispatches this with
    // source: 'product-form-component' (see assets/product-form.js), except
    // the "Buy as a set" block, handled below. Cart-line
    // quantity edits and upsell confirms reuse the same event name, so those
    // are excluded here and handled separately below / in add-to-cart-upsell.js.
    if (data.source === 'product-form-component' && !data.didError) {
      const item = readItem(target);
      const quantity = data.itemCount || 1;
      const value = item?.price != null ? item.price * quantity : undefined;

      // Diagnostic only — doesn't touch the emitted event. A handful of
      // production add_to_cart_detail rows have shown implausibly large,
      // round `value`s (see the event tracking plan doc); this surfaces the
      // raw inputs in the console the next time it happens so the root
      // cause (bad price? bad quantity?) can be pinned down definitively.
      if (value != null && value > 10000) {
        console.warn('[analytics] Implausible add_to_cart_detail value', {
          value,
          price: item?.price,
          quantity,
          itemCount: data.itemCount,
          itemId: item?.item_id,
          productId: /** @type {any} */ (target)?.dataset?.productId,
        });
      }

      pushDataLayerEvent('add_to_cart_detail', {
        component_source: componentSourceFor(target),
        currency: currentCurrency(),
        value,
        items: item ? [{ ...item, quantity }] : [],
      });
      return;
    }

    // "Buy as a set" block (assets/buy-set.js) adds several prints at once,
    // one of each, and passes their data-analytics-item-* snapshots along
    // (item_id is the variant id, as for a regular add-to-cart), plus the set
    // collection's handle/title as item_list_id/item_list_name so the set is
    // identified explicitly rather than from the page URL.
    if (data.source === 'buy-set-block' && !data.didError) {
      const items = (Array.isArray(data.analyticsItems) ? data.analyticsItems : [])
        .map((dataset) => itemFromDataset(dataset))
        .filter((item) => item !== null)
        .map((item) => ({ ...item, quantity: 1 }));
      const value = items.reduce((sum, item) => sum + (item.price ?? 0), 0);

      pushDataLayerEvent('add_to_cart_detail', {
        component_source: componentSourceFor(target),
        item_list_id: data.analyticsListId || undefined,
        item_list_name: data.analyticsListName || undefined,
        currency: currentCurrency(),
        value: items.length ? value : undefined,
        items,
      });
      return;
    }

    // Cart-line quantity changes and removals both go through
    // cart-items-component; only a removal (quantity -> 0) counts as
    // remove_from_cart_detail (see assets/component-cart-items.js).
    if (data.source === 'cart-items-component' && data.action === 'clear') {
      const item = itemFromDataset(data.removedItem);
      const quantity = Number(data.removedItem?.analyticsItemQuantity) || 1;
      pushDataLayerEvent('remove_from_cart_detail', {
        component_source: componentSourceFor(target),
        currency: currentCurrency(),
        value: item?.price != null ? item.price * quantity : undefined,
        items: item ? [{ ...item, quantity }] : [],
      });
    }
  });

  /* ------------------------------------------------------------------ */
  /* Cart: view_cart_detail / begin_checkout_detail                      */
  /* ------------------------------------------------------------------ */

  // Cart *page* view: the page loading is the "viewed" moment. The drawer's
  // equivalent moment is handled in assets/cart-drawer.js (open() is a real
  // interaction, unlike a page load).
  if (document.querySelector('[data-analytics-cart-source="cart_page"]')) {
    pushDataLayerEvent('view_cart_detail', {
      component_source: 'cart_page',
      currency: currentCurrency(),
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const checkoutButton = target?.closest('.cart__checkout-button');
    if (!checkoutButton) return;

    pushDataLayerEvent('begin_checkout_detail', {
      component_source: componentSourceFor(checkoutButton),
      currency: currentCurrency(),
    });
  });

  /* ------------------------------------------------------------------ */
  /* Browse & discovery: view_item_list / select_item                    */
  /* (homepage featured grid, collection grid, search results, PDP       */
  /* "similar products" carousel — see the data-analytics-list-id        */
  /* markers added to those sections)                                    */
  /* ------------------------------------------------------------------ */

  /** @param {Element} container */
  function listPayload(container) {
    const { analyticsListId, analyticsListSource, analyticsPageType } = /** @type {HTMLElement} */ (container)
      .dataset;
    return {
      item_list_id: analyticsListId,
      component_source: analyticsListSource,
      page_type: analyticsPageType,
    };
  }

  /** @type {WeakMap<Element, Set<string>>} Item ids already reported per list container. */
  const reportedListItems = new WeakMap();

  /** @type {WeakSet<Element>} Containers that have actually crossed the visibility threshold. */
  const intersectedListContainers = new WeakSet();

  /**
   * Reports any not-yet-reported product-cards currently inside a list
   * container. Called on first intersection, and again whenever the
   * container's contents change (infinite scroll, or a lazily-fetched
   * component like PDP recommendations finishing its render) — so items
   * that weren't in the DOM yet at first intersection still get reported,
   * and nothing is reported twice. A container's contents can change
   * before it's actually been scrolled into view (e.g. recommendations
   * fetched while still below the fold) — skip those until the container
   * has genuinely intersected, so view_item_list isn't sent for a list
   * that hasn't been seen yet.
   * @param {Element} container
   */
  function reportListItems(container) {
    if (!intersectedListContainers.has(container)) return;

    const reported = reportedListItems.get(container) ?? new Set();

    // Scoped to product-card specifically (not the generic
    // [data-analytics-item-id] selector) — each card also has a nested
    // quick-add <product-form-component> carrying the same attributes,
    // which would otherwise be double-counted here.
    const newItems = Array.from(container.querySelectorAll('product-card[data-analytics-item-id]'))
      .filter((card) => {
        const itemId = /** @type {HTMLElement} */ (card).dataset.analyticsItemId;
        return itemId && !reported.has(itemId);
      })
      .map((card) => {
        const item = readItem(card);
        if (item?.item_id) reported.add(item.item_id);
        return item ? { ...item, quantity: 1 } : null;
      })
      .filter(Boolean);

    reportedListItems.set(container, reported);
    if (newItems.length === 0) return;

    pushDataLayerEvent('view_item_list', {
      ...listPayload(container),
      items: newItems,
    });
  }

  const listObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        intersectedListContainers.add(entry.target);
        reportListItems(entry.target);
      }
    },
    { threshold: 0.1 }
  );

  const listMutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // mutation.target is whatever descendant node's children changed
      // (e.g. a paginated-list's inner grid, not the outer container that
      // actually carries data-analytics-list-id) — resolve back up to it.
      const container = mutation.target instanceof Element ? mutation.target.closest('[data-analytics-list-id]') : null;
      if (container) reportListItems(container);
    }
  });

  document.querySelectorAll('[data-analytics-list-id]').forEach((container) => {
    listObserver.observe(container);
    listMutationObserver.observe(container, { childList: true, subtree: true });
  });

  // Listens on the <product-card> itself rather than its a.product-card__link
  // — that link is a full-card overlay positioned *behind* .product-card__content
  // (see assets/base.css), so real clicks land on the visible content and
  // never reach it. Navigation itself is handled the same way, by a click
  // listener on <product-card> (see ProductCard#navigateToProduct in
  // assets/product-card.js) — the interactive-element/no-navigation checks
  // below mirror that gating so this only fires for clicks that actually
  // select the card.
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const card = target?.closest('product-card[data-analytics-item-id]');
    if (!card || card.hasAttribute('data-no-navigation')) return;
    if (target?.closest('button, input, label, select, [tabindex="1"]')) return;

    const listContainer = card.closest('[data-analytics-list-id]');
    if (!listContainer) return;

    const item = readItem(card);
    pushDataLayerEvent('select_item', {
      ...listPayload(listContainer),
      items: item ? [{ ...item, quantity: 1 }] : [],
    });
  });

  /* ------------------------------------------------------------------ */
  /* Browse & discovery: component_viewed / component_clicked             */
  /* (header nav, mega menu, submenu, footer links, and promotional      */
  /* banners/carousels — hero, slideshow, collection-list sections)      */
  /* ------------------------------------------------------------------ */

  const componentObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        componentObserver.unobserve(entry.target);

        const { analyticsComponentSource, analyticsComponentLabel } = /** @type {HTMLElement} */ (entry.target)
          .dataset;
        pushDataLayerEvent('component_viewed', {
          component_source: analyticsComponentSource,
          label: analyticsComponentLabel,
        });
      }
    },
    { threshold: 0.1 }
  );

  // header_nav is sticky-header content on desktop — already in the
  // viewport at page load, so scroll detection would just delay (never
  // change) the outcome; report it at mount instead. Its mobile counterpart
  // shares the same component_source but lives inside the closed <details>
  // drawer (see snippets/header-drawer.liquid), which the browser hides
  // natively (zero client rects) until it's opened — so `isRendered` gates
  // the mount-fire to instances actually on screen right now, and a closed
  // drawer's nav falls through to the scroll/visibility observer below,
  // firing once the drawer is actually opened.
  const componentViewedOnMount = new Set(['header_nav']);

  /** @param {Element} el */
  function isRendered(el) {
    return el.getClientRects().length > 0;
  }

  // Desktop's mega menu / submenu panels (.mega-menu / .menu-list__submenu)
  // never leave the layout — they're only hidden with `visibility: hidden`
  // until hover/focus reveals them (see blocks/_header-menu.liquid). Their
  // geometry never changes, so neither IntersectionObserver nor a
  // getClientRects() check can tell they're invisible, and both would
  // wrongly report every mega menu/submenu as viewed at page load. Track
  // those on the same pointerenter/focus interaction the header menu
  // component itself uses to reveal them (assets/header-menu.js) instead.
  // The mobile drawer's equivalent panels (.menu-drawer__submenu /
  // .menu-drawer__explore) are genuinely removed from the render tree until
  // opened (closed <details>), so they still work correctly through the
  // observer below.
  const DESKTOP_HOVER_REVEALED_SELECTOR = '.menu-list__submenu, .mega-menu';
  const revealReported = new WeakSet();

  /** @param {Element} li */
  function reportRevealedComponents(li) {
    // `:is(...)` so the attribute requirement applies to every branch of the
    // selector list, not just the last one — a bare
    // `${DESKTOP_HOVER_REVEALED_SELECTOR}[data-analytics-component-source]`
    // only attaches the attribute filter to `.mega-menu`, leaving
    // `.menu-list__submenu` unfiltered.
    li.querySelectorAll(`:is(${DESKTOP_HOVER_REVEALED_SELECTOR})[data-analytics-component-source]`).forEach((el) => {
      if (revealReported.has(el)) return;
      revealReported.add(el);

      const { analyticsComponentSource, analyticsComponentLabel } = /** @type {HTMLElement} */ (el).dataset;
      pushDataLayerEvent('component_viewed', {
        component_source: analyticsComponentSource,
        label: analyticsComponentLabel,
      });
    });
  }

  // pointerenter doesn't bubble, but a capturing listener still receives it
  // during the capture phase — matching the on:pointerenter binding
  // snippets/header-menu.liquid attaches per menu item. focusin is used
  // instead of focus specifically because it does bubble, so a single
  // listener on headerGroup is enough. Scoped to headerGroup (rather than
  // document) so these delegated listeners don't run on every pointer
  // transition/focus change on the whole page.
  const headerGroup = document.getElementById('header-group');
  headerGroup?.addEventListener(
    'pointerenter',
    (event) => {
      const li = event.target instanceof Element ? event.target.closest('.menu-list__list-item') : null;
      if (li) reportRevealedComponents(li);
    },
    true
  );
  headerGroup?.addEventListener('focusin', (event) => {
    const li = event.target instanceof Element ? event.target.closest('.menu-list__list-item') : null;
    if (li) reportRevealedComponents(li);
  });

  document.querySelectorAll('[data-analytics-component-source]').forEach((el) => {
    if (el.matches(DESKTOP_HOVER_REVEALED_SELECTOR)) return;

    const { analyticsComponentSource, analyticsComponentLabel } = /** @type {HTMLElement} */ (el).dataset;
    if (componentViewedOnMount.has(analyticsComponentSource) && isRendered(el)) {
      pushDataLayerEvent('component_viewed', {
        component_source: analyticsComponentSource,
        label: analyticsComponentLabel,
      });
    } else {
      componentObserver.observe(el);
    }
  });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('a[href]');
    if (!link) {
      // Button CTAs inside a tracked component that open something in place
      // rather than navigating (e.g. the cart frame upsell hero's button,
      // which opens the upsell modal — see blocks/frame-upsell-hero-cart.liquid).
      const cta = target?.closest('button[data-analytics-cta]');
      const component = cta?.closest('[data-analytics-component-source]');
      if (component instanceof HTMLElement) {
        pushDataLayerEvent('component_clicked', {
          component_source: component.dataset.analyticsComponentSource,
          label: component.dataset.analyticsComponentLabel,
        });
      }
      return;
    }

    const inHeader = link.closest('#header-group');
    const inFooter = link.closest('#footer-group');

    // Promotional banners/carousels (hero, slideshow, collection-list) and
    // the PDP add-to-cart block: report at the component level
    // (component_source + its derived label), plus which specific tile was
    // clicked when the component exposes one (e.g. a collection-list
    // carousel's collection-card tiles carry data-analytics-block-id/-label
    // — see snippets/collection-card.liquid). Excludes header/footer links,
    // which have their own more specific classification below — now that
    // #header-group/#footer-group and their submenus also carry
    // data-analytics-component-source for view tracking, they'd otherwise
    // match this first and shadow that classification (e.g. losing the
    // actual link text label in favor of the component's generic one).
    if (!inHeader && !inFooter) {
      const component = link.closest('[data-analytics-component-source]');
      if (component instanceof HTMLElement) {
        const block = link.closest('[data-analytics-block-id]');
        pushDataLayerEvent('component_clicked', {
          component_source: component.dataset.analyticsComponentSource,
          label: component.dataset.analyticsComponentLabel,
          link_url: link.getAttribute('href'),
          block_id: block instanceof HTMLElement ? block.dataset.analyticsBlockId : undefined,
          block_label: block instanceof HTMLElement ? block.dataset.analyticsBlockLabel : undefined,
        });
        return;
      }
    }

    if (!inHeader && !inFooter) return;

    // Header nav / mega menu / submenu / footer links. Desktop nav uses
    // .menu-list__*/.mega-menu__*/.explore-menu__* (see
    // snippets/header-menu.liquid, mega-menu.liquid, mega-menu-explore.liquid).
    // Mobile uses the separate header-drawer component with .menu-drawer__*
    // classes (see snippets/header-drawer.liquid) — both need covering.
    let componentSource;
    if (inFooter) {
      componentSource = 'footer_link';
    } else if (link.closest('.mega-menu__link, .explore-menu__link, .menu-drawer__explore')) {
      componentSource = 'header_mega_menu';
    } else if (link.closest('.menu-list__submenu, .menu-drawer__submenu')) {
      componentSource = 'header_submenu';
    } else if (link.closest('.menu-list__link, .menu-drawer__menu-item')) {
      componentSource = 'header_nav';
    } else {
      return;
    }

    pushDataLayerEvent('component_clicked', {
      component_source: componentSource,
      label: link.textContent?.trim(),
      link_url: link.getAttribute('href'),
    });
  });
}

// Set synchronously in <head> (see layout/theme.liquid) only when
// settings.ga4_measurement_id is configured, before this module (a
// deferred module script, or an unconditional import from cart-drawer.js /
// add-to-cart-upsell.js) evaluates. A theme-owned flag rather than
// `typeof window.gtag === 'function'` — some other app/theme script could
// also define window.gtag for an unrelated GA property, which would
// wrongly enable this file's observers/listeners site-wide.
if (window.themeGa4Enabled === true) {
  init();
}
